import { create } from "zustand";
// import { immer } from 'zustand/middleware/immer'; // Optional: for easier state updates
import { ConversationSetting, Language, LANGUAGE_AUTO_DETECT } from '../../shared/types';
import { initBouncer, validateIncomingMessage, validateTranslation, getRejectionData } from '../../shared/bouncer';
import {
  AppState,
  AppStore,
  DisplayMessage,
  GameMessage,
  StampMessage,
  WebSocketMessageData,
  RoomData,
  User,
  Balance,
  SystemMessageData,
} from "@/types";
import {
  getSessionId,
  closeWebSocket,
  initializeWebSocket,
  sendWebSocketMessage,
  getConnectionState,
} from "@/services/socket"; // We'll create this
import {
  getUserColor,
  darkenHSL,
  createConsistentUsernameFromUUID,
} from "@/lib/userUtils";
import { toast } from "sonner"; // Using Sonner for QoS messages
import { clearAuthToken, getStoredAuthToken } from "../lib/auth";
import { clearAdminAuthToken, getInitialAdminUser } from "../lib/admin/auth";
import { ClientConfig } from "../../shared/types";
import { loadDefaultConfig } from "../../shared/configLoader";
import { supportedLanguages } from "@/components/layout/LanguageSelector";
import { useTranslation } from '@/lib/translate';

initBouncer(useTranslation);

function getDefaultLanguage(): Language {
  const lang = (navigator.language || (navigator as any).userLanguage || "en").toLowerCase();
  if (lang.startsWith("zh")) {
    return "zh";
  } else if (lang.startsWith("ja")) {
    return "ja";
  } else {
    return "en";
  }
}

function getDefaultTargetLanguage() {
  const defaultLang = getDefaultLanguage();
  return defaultLang;
}

function getConversationSetting(): ConversationSetting {
  try {
    const setting = localStorage.getItem("conversationSetting");
    return setting ? JSON.parse(setting) : { contextInput: "", presetOption: "" };
  } catch (error) {
    console.error("Error parsing conversation setting:", error);
    return { contextInput: "", presetOption: "" };
  }
}

const initialUser = getStoredAuthToken().user;
const initialAdminUser = getInitialAdminUser();
const initialLang = (localStorage.getItem("lang") as Language) || getDefaultLanguage();
const initialConversationSetting = getConversationSetting();
const initialSourceLanguage = (localStorage.getItem("source_lang") as Language) || LANGUAGE_AUTO_DETECT;
const initialTargetLanguages = JSON.parse(localStorage.getItem("target_langs") || "[]") as Language[];
if (initialTargetLanguages.length === 0) { // Migration or first load
  const legacyTarget = localStorage.getItem("target_lang") as Language;
  initialTargetLanguages.push(legacyTarget || getDefaultTargetLanguage());
}
const initialRoomId = localStorage.getItem("rid");
const initialSessionId = localStorage.getItem("sid");
const initialHasSeenTutorial = !!localStorage.getItem("tut");
const initialRoomHistory = JSON.parse(localStorage.getItem("rooms") || "{}");
const initialBalance = JSON.parse(localStorage.getItem("bal") || '{"free": 0, "premium": 0}');
const initialMicInput = localStorage.getItem("mic") || "";
const initialInputMode = (localStorage.getItem("inputMode") as "mic" | "keyboard") || "mic";
const initialLastSeenMessageId = 0; //parseInt(localStorage.getItem(`lastSeenMsgId:${initialRoomId}`) || "0");
const initialVADEagerness = (localStorage.getItem("vadEagerness") as "low" | "medium" | "high") || "medium";
const urlParams = new URLSearchParams(window.location.search);
const presentationParam = urlParams.get("presentation");
const initialIsPresentationMode = presentationParam === "true" || (presentationParam !== "false" && (
  import.meta.env.VITE_IS_PRESENTATION_MODE || window.location.hostname == "events.chotto.chat"
));

const initialState: AppState = {
  roomId: initialRoomId,
  sessionId: initialSessionId,
  user: initialUser,
  adminUser: initialAdminUser,
  conversationSetting: initialConversationSetting,
  preferredLanguage: initialLang,
  targetLanguages: initialTargetLanguages,
  sourceLanguage: initialSourceLanguage,
  messages: [],
  isConnected: false,
  isConnecting: false,
  isReconnecting: false,
  qosMessage: null,
  isTyping: false,
  isReady: false,
  hasChatlog: false, // Will be set true on first non-tutorial message
  hasSeenTutorial: initialHasSeenTutorial,
  roomHistory: initialRoomHistory,
  balance: initialBalance,
  micInput: initialMicInput,
  inputMode: initialInputMode,
  translatedMessage: null,
  lastSeenMessageId: initialLastSeenMessageId,
  isPresentationMode: initialIsPresentationMode,
  isMessagePopupOpen: false,
  focusedMessageId: initialLastSeenMessageId,
  isVADEnabled: false,
  isVADListening: false,
  isPIPActive: false,
  vadEagerness: initialVADEagerness,
  clientConfig: loadDefaultConfig(),
  isAutoTTS: false,
  //connected users
  connectedUsers: [] as Array<{ username: string, displayUsername?: string }>,
  presentationSettings: { vadEnabled: false, fontSize: '4xl', sidePanelOpen: false },
};

// let qosTimeout: ReturnType<typeof setTimeout> | null = null;
let typingTimeout: ReturnType<typeof setTimeout> | null = null;

// Auto-reconnection state
let reconnectAttempts = 0;
let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_BACKOFF_MS = [1000, 2000, 3000, 5000, 8000]; // Exponential backoff delays

/**
 * Attempts auto-reconnect with exponential backoff.
 * Returns true if a retry was scheduled, false if max attempts exhausted.
 */
const attemptAutoReconnect = (
  get: () => AppStore,
  roomId: string | null,
  reason: string
): boolean => {
  if (reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
    const delay = RECONNECT_BACKOFF_MS[reconnectAttempts] || RECONNECT_BACKOFF_MS[RECONNECT_BACKOFF_MS.length - 1];
    console.log(`${reason}, auto-retry ${reconnectAttempts + 1}/${MAX_RECONNECT_ATTEMPTS} in ${delay}ms`);
    reconnectAttempts++;

    reconnectTimeout = setTimeout(() => {
      const state = get();
      if (!state.isConnected && !state.isConnecting && roomId) {
        console.log("Attempting silent auto-reconnect...");
        state.connectWebSocket(roomId);
      }
    }, delay);
    return true; // Retry scheduled
  }
  console.log(`Max reconnect attempts reached (${reason}), showing overlay`);
  return false; // Max attempts exhausted
};

export const useAppStore = create<AppStore>()(
  // immer( // Uncomment if using immer middleware
  (set, get) => ({
    ...initialState,

    // --- ACTIONS ---

    connectWebSocket: async (roomIdToJoin: string | null = null) => {
      const currentRoomId = get().roomId;
      const currentSessionId = get().sessionId;
      const user = get().adminUser || get().user;
      const isAdmin = user?.role == "admin";
      if (!user || !user.userId) {
        alert("Not logged in");
        return;
      }
      const userId = user.userId;

      let targetRoomId = roomIdToJoin ?? currentRoomId;
      let targetSessionId = currentSessionId;
      let isNewRoom = false;

      // Check current connection state from socket service
      const socketState = getConnectionState();

      // If we're already connecting to the same session, don't start another connection
      if (socketState.isConnecting && socketState.currentSessionId === targetSessionId) {
        console.log("Already connecting to the same session, waiting...");
        return;
      }

      // If explicitly joining a new room OR no session exists for the current room
      if (roomIdToJoin || (targetRoomId && !currentSessionId)) {
        console.log(`Attempting to get session for room: ${targetRoomId}`);
        set({ isConnecting: true, isReconnecting: false, isReady: false });
        try {
          const sessionData = await getSessionId(
            isAdmin,
            targetRoomId === "new" ? null : targetRoomId,
            currentRoomId
          ); // 'new' means create
          if (!sessionData) throw new Error("Failed to obtain session.");

          targetRoomId = sessionData.roomId;
          targetSessionId = sessionData.sessionId;
          isNewRoom = true; // Flag to clear chat log later

          // Store new session details
          localStorage.setItem("rid", targetRoomId);
          localStorage.setItem("sid", targetSessionId);
          set({ roomId: targetRoomId, sessionId: targetSessionId });
          console.log(
            `Obtained session: ${targetSessionId} for room: ${targetRoomId}`
          );
        } catch (error: any) {
          console.error("Session acquisition failed:", error);
          set({ isConnecting: false });

          // Apply auto-reconnect logic for session fetch failures
          const retryScheduled = attemptAutoReconnect(get, roomIdToJoin || targetRoomId, "Session fetch failed");
          if (!retryScheduled) {
            get().showQosMessage(
              `Error joining room: ${error.message || "Please try again."}`
            );
            // Clear potentially invalid stored IDs if we failed to join/get session
            localStorage.removeItem("rid");
            localStorage.removeItem("sid");
            set({ roomId: null, sessionId: null, isReconnecting: true });
          }
          return; // Stop connection attempt
        }
      } else if (!targetRoomId || !targetSessionId) {
        console.warn("Cannot connect: No room ID or session ID available.");
        return;
      }

      // Check if we're already connected to the same session
      if (socketState.isConnected && socketState.currentSessionId === targetSessionId) {
        console.log("Already connected to the same session.");
        set({ isConnecting: false, isConnected: true }); // Ensure state is in sync
        return; // Already connected
      }

      set({ isConnecting: true, isReconnecting: false });
      const roomId = get().roomId;
      initializeWebSocket(isAdmin, targetSessionId, {
        onOpen: () => {
          console.log("WebSocket Connected");
          // Reset reconnection state on successful connection
          reconnectAttempts = 0;
          if (reconnectTimeout) {
            clearTimeout(reconnectTimeout);
            reconnectTimeout = null;
          }
          set({
            isConnected: true,
            isConnecting: false,
            isReconnecting: false,
          });
          // Send initial handshake/setup messages
          sendWebSocketMessage({
            type: "setUsername",
            username: userId,
            userType: user.provider,
            role: user.role,
            sourceLanguage: get().sourceLanguage,
            targetLanguages: get().targetLanguages,
            room: roomId
          });
          if (isNewRoom) {
            set({ messages: [], hasChatlog: false, isReady: false }); // Clear chat on new room join
          }
          // get().showQosMessage("Connected!", 2000);
          // Trigger tutorial if needed
          if (!get().hasSeenTutorial && !get().hasChatlog) {
            // TODO: Implement tutorial logic here or in a component effect
            // showTutorial(set); // Pass set to update state during tutorial
            localStorage.setItem("tut", "true");
            set({ hasSeenTutorial: true });
          }
          roomId && get().addRoomHistory(roomId);
        },
        onMessage: (data: WebSocketMessageData | Blob) => {
          if (typingTimeout) clearTimeout(typingTimeout);
          set({ isTyping: false }); // Hide typing indicator on any message

          if (data instanceof Blob) {
            console.log("Received audio blob");
            // TODO: Trigger audio playback hook/logic
            // useAudioPlayer.getState().queueAudio(data);
            return;
          }

          console.log(
            "%cReceived message:",
            "background: #d4f8c4; color: #1b5e20; padding: 2px 6px; border-radius: 3px;",
            data
          );
          // const currentMessages = get().messages;

          switch (data.type) {
            case "message":
              if (!data.isTutorial) {
                set({ hasChatlog: true }); // Mark that real chat has started
              }

              // BOUNCER: Validate message and all translations
              const { message: finalMessage, translations: finalTranslations, bouncerRejected } =
                validateIncomingMessage(data.message, data.translations, get().targetLanguages, get().preferredLanguage);

              const userColor = getUserColor(data.username, userId, get().clientConfig);
              const displayMessage: DisplayMessage = {
                ...data,
                message: finalMessage,
                id: data.timestamp.toString(), // Use timestamp as unique ID
                displayUsername: data.displayUsername || createConsistentUsernameFromUUID(
                  data.username
                ),
                isMe: !isAdmin && data.username === userId,
                isRead: false,
                userColor: userColor,
                userColorDarker: userColor, //darkenHSL(userColor),
                translations: finalTranslations,
                bouncerRejected: bouncerRejected || undefined,
              };
              // Add to the beginning of the array
              set((state) => ({
                messages: [displayMessage, ...state.messages],
              }));
              break;

            case "game":
              const gameMessage: GameMessage = {
                ...data,
                id: data.session,
              };
              set((state) => {
                const { messages } = state;
                const index = messages.findIndex((m) => m.type === 'game' && m.session === data.session);
                if (index >= 0) messages.splice(index, 1);
                return { messages: [gameMessage, ...messages] };
              });
              break;

            case "stamp":
              const stampMessage: StampMessage = {
                ...data,
                id: data.timestamp.toString(), // Use timestamp as unique ID
                displayUsername: createConsistentUsernameFromUUID(
                  data.username
                ),
                isMe: data.username === userId
              };
              set((state) => ({
                messages: [stampMessage, ...state.messages],
              }));
              break;

            case "system":
              console.log(`System Message: ${data.message}`);
              const roomId = get().roomId;
              const interfaceLanguage = get().preferredLanguage;
              const t = useTranslation(interfaceLanguage);
              if (data.message === "READY") {
                set({ isReady: true });
                set({ isConnecting: false }); // Ensure connecting state is off
                get().showQosMessage(t("Connected to room {{roomId}}", { roomId: roomId || "" }), 1500);
                const sourceLanguage = get().sourceLanguage;
                const targetLanguages = get().targetLanguages;
                sendWebSocketMessage({
                  type: "setLanguage",
                  sourceLanguage: sourceLanguage,
                  targetLanguages: targetLanguages,
                  interfaceLanguage: interfaceLanguage,
                  room: roomId
                });
              }
              if (data.message === "BALANCE") {
                set({ balance: data.balance });
                localStorage.setItem("bal", JSON.stringify({ ...data.balance }));
              }
              if (data.message === "JOIN" || data.message == "LEAVE") {
                const userStatusMessage: SystemMessageData = {
                  ...data,
                  displayUsername: data.displayUsername || createConsistentUsernameFromUUID(
                    data.username
                  ),
                };
                // Add to the messages array
                set((state) => ({
                  messages: [userStatusMessage, ...state.messages],
                }));

              }
              // Optionally display system messages in chat or handle differently
              // Example: Add a specific type to DisplayMessage?
              break;

            case "translationChunk":
            case "translationComplete":
              let translatedMessage: DisplayMessage;
              set((state) => {
                const messages = state.messages.map((msg) => {
                  if (msg.type !== "message") return msg
                  if (msg.id !== data.originalTimestamp.toString()) return msg;

                  if (data.type === "translationChunk") {
                    // For chunks, just append (validation happens on complete)
                    const newTranslations = {
                      ...msg.translations,
                      [data.targetLanguage]: (msg.translations[data.targetLanguage] || "") + data.chunk,
                    };
                    translatedMessage = { ...msg, translations: newTranslations };
                    return translatedMessage;
                  }

                  // BOUNCER: On complete, validate - if invalid, replace entire bubble
                  const rejection = validateTranslation(data.fullText || '', get().targetLanguages, get().preferredLanguage);
                  if (rejection) {
                    translatedMessage = { ...msg, ...rejection };
                    return translatedMessage;
                  }

                  const newTranslations = {
                    ...msg.translations,
                    [data.targetLanguage]: data.fullText || useTranslation(data.targetLanguage)("[Could not transcribe the speech]"),
                  };
                  translatedMessage = { ...msg, translations: newTranslations };
                  return translatedMessage;
                });

                if (data.type == "translationComplete" && translatedMessage) {
                  return {
                    messages,
                    balance: get().balance,
                    translatedMessage
                  };
                }

                return { messages, balance: get().balance };
              });
              break;
            case "noteComplete":
              set((state) => {
                const msgIndex = state.messages.findIndex(
                  (m) => m.type == "message" && m.id === data.originalTimestamp.toString()
                );
                if (msgIndex > -1) {
                  const updatedMessages = [...state.messages];
                  const targetMessage = updatedMessages[msgIndex] as DisplayMessage;
                  const notes = {
                    ...targetMessage.notes,
                    [data.targetLanguage]: data.note
                  }
                  targetMessage.notes = notes;
                  updatedMessages[msgIndex] = { ...targetMessage }; // Ensure re-render
                  return { messages: updatedMessages };
                }
                return {}; // No change if original message not found
              });
              break;
            case "usernameUpdate":
              const myMsg = data.oldDisplayUsername ? `Username updated from ${data.oldDisplayUsername} to ${data.displayUsername}.`
                : `Username updated to ${data.displayUsername}.`;
              const othersMsg = `User ${data.oldDisplayUsername || createConsistentUsernameFromUUID(data.username)} updated username to ${data.displayUsername}`;
              const toastMsg = data.username === userId ? myMsg : othersMsg;

              get().showQosMessage(toastMsg);
              set((state) => {
                const messages = state.messages.map((msg) => {
                  if (msg.type !== "game" && msg.username == data.username) {
                    return {
                      ...msg,
                      displayUsername: data.displayUsername
                    };
                  } else {
                    return msg;
                  }
                });
                return { messages };
              });
              break;
            case "messageTranslated":
              set((state) => {
                const msgIndex = state.messages.findIndex(
                  (m) => m.type == "message" && m.id === data.originalTimestamp.toString()
                );
                if (msgIndex > -1) {
                  const updatedMessages = [...state.messages];
                  const targetMessage = updatedMessages[msgIndex] as DisplayMessage;
                  // BOUNCER: Validate translation - if invalid, replace entire bubble
                  const rejection = validateTranslation(data.fullText || '', get().targetLanguages, get().preferredLanguage);
                  if (rejection) {
                    updatedMessages[msgIndex] = { ...targetMessage, ...rejection };
                  } else {
                    targetMessage.translations[data.targetLanguage] = data.fullText || useTranslation(data.targetLanguage)("[Could not transcribe the speech]");
                    updatedMessages[msgIndex] = { ...targetMessage };
                  }
                  return { messages: updatedMessages };
                }
                return {}; // No change if original message not found
              });
              break;
            case "messageDeleted":
              set((state) => {
                const messages = state.messages.filter((msg) => {
                  return msg.timestamp.toString() !== data.originalTimestamp.toString()
                });
                return { messages };
              });
              break;
            case "userUpdate":
              set({ connectedUsers: data.userList });
              break;
            case "reactMessage": {
              set((state) => {
                state.messages.forEach((msg) => {
                  if (msg.type === "message" && msg.id === data.timestamp.toString()) {
                    msg.isLiked = data.isLike;
                  }
                });

                return { messages: [...state.messages] };
              });
              break;
            }
            case "commentMessage": {
              set((state) => {
                state.messages.forEach((msg) => {
                  if (msg.type === "message" && msg.id === data.timestamp.toString()) {
                    msg.comment = data.comment;
                    msg.commentType = data.commentType;
                    msg.email = data.email;
                    (msg as any).expectedTranscription = data.expectedTranscription;
                    (msg as any).expectedTranslation = data.expectedTranslation;
                    (msg as any).targetLanguage = data.targetLanguage;
                  }
                });

                return { messages: [...state.messages] };
              });
              break;
            }
            case "setConversationContext": {
              set((state) => {
                state.conversationSetting = {
                  contextInput: data.contextInput,
                  presetOption: data.presetOption,
                };
                sendWebSocketMessage({
                  type: "setConversationContext",
                  timestamp: Date.now(),
                  contextInput: data.contextInput,
                  presetOption: data.presetOption,
                  shouldBroadcast: false
                })

                // Store raw data for dynamic translation in SystemBubble
                const systemMsg: SystemMessageData = {
                  type: "system",
                  timestamp: Date.now(),
                  message: "CONTEXT_CHANGE", // Marker for SystemBubble to know this needs translation
                  username: "_system",
                  contextData: {
                    presetOption: data.presetOption,
                    contextInput: data.contextInput
                  }
                };

                return {
                  conversationSetting: { ...state.conversationSetting },
                  messages: [systemMsg, ...state.messages]
                };
              });
              break;
            }
            case "setPresentationSettings": {
              set((state) => {
                // We ignore completly `data.settings.vadEnabled` for the time
                // being because of the scenarios where it does not work, particularly
                // when the page is loading and user did not interact yet
                let vEnabled = state.presentationSettings.vadEnabled;

                return {
                  presentationSettings: {
                    ...state.presentationSettings,
                    ...data.settings,
                    vadEnabled: vEnabled
                  }
                } as Partial<AppStore>;
              });
              break;
            }
          }
        },
        onClose: (event: CloseEvent) => {
          console.log("WebSocket Closed:", event.code, event.reason);
          set({ isConnected: false, isConnecting: false });

          // Handle different close codes
          if (event.code !== 1000) {
            // 1000 is normal closure, anything else might need reconnection
            console.log("Unexpected close, may need to reconnect");

            const retryScheduled = attemptAutoReconnect(get, get().roomId, "WebSocket closed unexpectedly");
            if (!retryScheduled) {
              set({ isReconnecting: true });
            }
          } else {
            // Normal closure - reset reconnect state
            reconnectAttempts = 0;
            currentRoomId && get().showQosMessage("Disconnected from " + currentRoomId, 2000);
          }
        },
        onError: (error: Event) => {
          console.error("WebSocket Error:", error);
          set({
            isConnected: false,
            isConnecting: false,
          });

          const retryScheduled = attemptAutoReconnect(get, get().roomId, "WebSocket error");
          if (!retryScheduled) {
            set({ isReconnecting: true });
          }
        },
      });
    },

    disconnectWebSocket: (isReconnecting = false) => {
      console.log("Disconnecting WebSocket...");
      // Clear any pending auto-reconnect
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
      }
      reconnectAttempts = 0;
      closeWebSocket();
      set({
        isConnected: false,
        isConnecting: false,
        isReconnecting,
        isReady: false
      });
    },

    sendMessage: (text: string, username?: string) => {
      if (!get().isConnected) {
        console.warn("Cannot send message: WebSocket not connected.");
        get().showQosMessage("Not connected.", 2000);
        return;
      }
      const message: any = {
        type: "chat",
        language: LANGUAGE_AUTO_DETECT, //get().sourceLanguage,
        message: text,
        room: get().roomId,
      };
      // username is passed in admin mode
      if (username) {
        message.username = username;
      }
      sendWebSocketMessage(message);
      set({ isTyping: true }); // Show typing indicator optimistically
      if (typingTimeout) clearTimeout(typingTimeout);
      typingTimeout = setTimeout(() => set({ isTyping: false }), 5000); // Auto-hide after 5s
    },

    sendGameEvent: (gameId: string, session: string, data: any) => {
      if (!get().isConnected) {
        console.warn("Cannot send message: WebSocket not connected.");
        get().showQosMessage("Not connected.", 2000);
        return;
      }
      const message = {
        type: "game",
        gameId: gameId,
        session: session,
        data: data,
        room: get().roomId,
      };
      sendWebSocketMessage(message);
    },

    sendStamp: (stampId: string) => {
      if (!get().isConnected) {
        console.warn("Cannot send message: WebSocket not connected.");
        get().showQosMessage("Not connected.", 2000);
        return;
      }
      sendWebSocketMessage({
        type: "stamp",
        stampId: stampId,
        room: get().roomId,
      });
    },

    sendAudio: (blob: Blob) => {
      if (!get().isConnected) {
        console.warn("Cannot send audio: WebSocket not connected.");
        get().showQosMessage("Not connected.", 2000);
        return;
      }
      console.log("Sending audio blob:", blob.size, "bytes");
      sendWebSocketMessage(blob); // Send blob directly
      set({ isTyping: true }); // Show typing indicator
      if (typingTimeout) clearTimeout(typingTimeout);
      typingTimeout = setTimeout(() => set({ isTyping: false }), 5000);
    },

    setPreferredLanguage: (lang: Language) => {
      localStorage.setItem("lang", lang);
      set({ preferredLanguage: lang });
      if (get().isConnected) {
        sendWebSocketMessage({
          type: "setLanguage",
          targetLanguages: get().targetLanguages,
          sourceLanguage: get().sourceLanguage,
          interfaceLanguage: lang,
          room: get().roomId
        });
      }
    },

    isPreferredLanguageSet: () => {
      const lang = localStorage.getItem("lang");
      const items: string[] = supportedLanguages.map(item => item.code);
      return !!lang && items.indexOf(lang) >= 0;
    },

    setTargetLanguages: (langs: Language[]) => {
      localStorage.setItem("target_langs", JSON.stringify(langs));
      set({ targetLanguages: langs });

      // Update bouncer-rejected messages to show error in new first language
      set((state) => {
        const updatedMessages = state.messages.map((msg) => {
          if (msg.type === 'message' && msg.bouncerRejected) {
            return { ...msg, ...getRejectionData(langs, get().preferredLanguage) };
          }
          return msg;
        });
        return { messages: updatedMessages };
      });

      if (get().isConnected) {
        sendWebSocketMessage({
          type: "setLanguage",
          targetLanguages: langs,
          sourceLanguage: get().sourceLanguage,
          interfaceLanguage: get().preferredLanguage,
          room: get().roomId
        });
      }
    },

    reactTranslation: (isLike: boolean) => {
      get().showQosMessage(`reactTranslation: ${isLike ? "like" : "dislike"}`, 2000);
    },

    setSourceLanguage: (lang: Language) => {
      localStorage.setItem("source_lang", lang);
      set({ sourceLanguage: lang });
      if (get().isConnected) {
        sendWebSocketMessage({
          type: "setLanguage",
          sourceLanguage: lang,
          targetLanguages: get().targetLanguages,
          interfaceLanguage: get().preferredLanguage,
          room: get().roomId
        });
      }
    },

    setLastSeenMessageId: (roomId: string, msgId: number) => {
      // localStorage.setItem(`lastSeenMsgId:${roomId}`, String(msgId));
      set({ lastSeenMessageId: msgId });
    },

    setRoomId: (roomId: string | null) => {
      // This only updates the state/local storage, doesn't trigger connection
      if (roomId) {
        localStorage.setItem("rid", roomId);
      } else {
        localStorage.removeItem("rid");
      }
      set({ roomId });
    },

    redirectToRoom: (roomId: string, isAdmin?: boolean) => {
      if (isAdmin) {
        window.location.href = `/admin?r=${roomId}`;
      } else {
        window.location.href = `/translate?r=${roomId}`;
      }
    },

    joinRoom: async (roomId: string) => {
      const socketState = getConnectionState();

      if (get().roomId === roomId && socketState.isConnected) {
        get().showQosMessage("Already in this room.", 2000);
        return;
      }

      // Prevent joining if already connecting to avoid race conditions
      if (socketState.isConnecting) {
        console.log("Already connecting, please wait...");
        get().showQosMessage("Already connecting, please wait...", 2000);
        return;
      }

      // Clear old session ID before connecting to new room
      localStorage.removeItem("rid");
      localStorage.removeItem("sid");
      set({ sessionId: null });
      await get().connectWebSocket(roomId); // connectWebSocket handles session fetching
    },

    createRoom: async () => {
      const socketState = getConnectionState();

      // Prevent creating room if already connecting to avoid race conditions
      if (socketState.isConnecting) {
        console.log("Already connecting, please wait...");
        get().showQosMessage("Already connecting, please wait...", 2000);
        return;
      }

      localStorage.removeItem("rid");
      localStorage.removeItem("sid");
      set({
        // roomId: null,
        sessionId: null
      });
      await get().connectWebSocket("new"); // 'new' signals creation intent
    },

    inspectRoom: async (roomId: string) => {
      const socketState = getConnectionState();

      if (get().roomId === roomId && socketState.isConnected) {
        get().showQosMessage("Already in this room.", 2000);
        return;
      }

      // Prevent joining if already connecting to avoid race conditions
      if (socketState.isConnecting) {
        console.log("Already connecting, please wait...");
        get().showQosMessage("Already connecting, please wait...", 2000);
        return;
      }

      set({ sessionId: null });
      await get().connectWebSocket(roomId, true); // connectWebSocket handles session fetching
    },

    showQosMessage: (text: string, duration: number = 3000) => {
      const id = Date.now(); // Unique ID for the toast
      toast(text, {
        id: id,
        duration: duration === -1 ? Infinity : duration, // Show indefinitely if duration is -1
      });
      // We don't strictly need to store it in state if using sonner,
      // but kept for potential direct rendering if needed.
      set({ qosMessage: { text, id } });
    },

    addRoomHistory: ((roomId: string) => {
      const roomsStr = localStorage.getItem("rooms");
      const roomData: RoomData = JSON.parse(roomsStr || "{}");
      if (roomData[roomId]) {
        roomData[roomId].updated = Date.now();
      } else {
        roomData[roomId] = { added: Date.now(), updated: Date.now() };
      }
      localStorage.setItem("rooms", JSON.stringify(roomData));
      set({ roomHistory: roomData });
      return Promise.resolve();
    }),

    clearRoomHistory: () => {
      localStorage.removeItem("rooms");
      set({ roomHistory: {} });
      return Promise.resolve();
    },

    setAdminUser: (user?: User) => {
      set({ adminUser: user });
    },

    setUser: (user?: User) => {
      set({ user: user });
    },

    setBalance: (balance: Balance) => {
      set({ balance });
      localStorage.setItem("bal", JSON.stringify({ ...balance }));
    },

    setPresentationMode: (flag: boolean) => {
      set({ isPresentationMode: flag });
    },

    setMessagePopupOpen: (flag: boolean) => {
      set({ isMessagePopupOpen: flag });
    },

    setFocusedMessageId(chatId: number) {
      set({ focusedMessageId: chatId });
    },

    setAutoTTS: (flag: boolean) => {
      set({ isAutoTTS: flag })
    },

    setVADEnabled: (enabled: boolean) => {
      set({ isVADEnabled: enabled });
    },

    setVADListening: (listening: boolean) => {
      set({ isVADListening: listening });
    },

    setVADEagerness: (eagerness: "low" | "medium" | "high") => {
      localStorage.setItem("vadEagerness", eagerness);
      set({ vadEagerness: eagerness });
    },

    toggleVAD: () => {
      // This will be overridden by PresentationChatLayout when VAD is available
      console.log("VAD toggle called but no VAD instance is registered");
    },

    setPIPActive: (active: boolean) => {
      set({ isPIPActive: active });
    },

    setPresentationSettings: (settings: any) => {
      set((state) => ({
        presentationSettings: {
          ...state.presentationSettings,
          ...settings
        }
      }));
      if (get().isConnected) {
        sendWebSocketMessage({
          type: "setPresentationSettings",
          settings: settings,
          roomId: get().roomId
        });
      }
    },

    setMicInput: (micInput: string) => {
      localStorage.setItem("mic", micInput);
      set({ micInput: micInput });
    },

    setInputMode: (mode: "mic" | "keyboard") => {
      localStorage.setItem("inputMode", mode);
      set({ inputMode: mode });
    },

    logout: () => {
      set({ user: undefined });
      get().disconnectWebSocket();
      clearAuthToken();
      window.location.href = "/login"
    },

    adminLogout: () => {
      set({ adminUser: undefined });
      get().disconnectWebSocket();
      clearAdminAuthToken();
      window.location.href = "/admin-login"
    },

    updateDisplayUsername: (username: string, displayUsername: string) => {
      sendWebSocketMessage({
        type: "updateUsername",
        username,
        displayUsername
      });
    },

    deleteMessage: (id: string) => {
      set((state) => ({
        messages: state.messages.filter((msg) => {
          // Only filter out if id matches and id exists
          return !("id" in msg && msg.id === id);
        })
      }));
      sendWebSocketMessage({
        type: "deleteMessage",
        timestamp: parseInt(id),
      });
    },
    // React to a message with like/dislike/comment
    reactMessage: (id: string, isLike: boolean | undefined) => {
      sendWebSocketMessage({
        type: "reactMessage",
        timestamp: parseInt(id),
        isLike
      });
    },
    commentMessage: (id: string, comment: string, commentType: string, email: string, expectedTranscription?: string, expectedTranslation?: string, targetLanguage?: string) => {
      sendWebSocketMessage({
        type: "commentMessage",
        timestamp: parseInt(id),
        comment,
        commentType,
        email,
        expectedTranscription,
        expectedTranslation,
        targetLanguage
      })
      get().showQosMessage('Submitted comment', 1000);
    },

    translateMessage: (id: string, language: Language) => {
      sendWebSocketMessage({
        type: "translateMessage",
        timestamp: parseInt(id),
        targetLanguage: language,
      });
    },

    readAllMessages: () => {
      set((state) => {
        const updatedMessages = state.messages.map((msg) => {
          if (msg.type === "message") {
            return { ...msg, isRead: true };
          }
          return msg;
        });
        return { messages: updatedMessages };
      });
    },

    countUnreadMessages: () => {
      const messages = get().messages;
      let count = 0;
      for (const msg of messages) {
        if (msg.type === "message" && !msg.isRead) {
          count++;
        }
      }
      return count;
    },

    setConversationContextSetting: (setting) => {
      sendWebSocketMessage({
        type: "setConversationContext",
        timestamp: Date.now(),
        contextInput: setting.contextInput,
        presetOption: setting.presetOption,
        shouldBroadcast: true
      });
      set((state) => {
        state.conversationSetting = setting;
        return { conversationSetting: { ...state.conversationSetting } };
      });
    },

    setClientConfig: (config: ClientConfig) => {
      set({ clientConfig: config })
    }
  })
  //) // Uncomment if using immer middleware
);

// Selector hook (optional but good practice)
export const useAppSelector = <T>(selector: (state: AppStore) => T): T => {
  return useAppStore(selector);
};
